import React, { forwardRef, useEffect, useRef } from 'react';
import { Search, X, CalendarDays, History, ChevronLeft, ChevronRight } from 'lucide-react';
import { CalendarCategory } from '../../services/calendarService';
import { ACCENT } from './eventUtils';

export type TimeMode = 'upcoming' | 'past';

export interface MonthOption {
  key: string;
  label: string;
}

interface EventsFilterBarProps {
  mode: TimeMode;
  onModeChange: (mode: TimeMode) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  categories: CalendarCategory[];
  selectedCategoryId: string | null;
  onSelectCategory: (id: string | null) => void;
  organizations: string[];
  selectedOrg: string | null;
  onSelectOrg: (org: string | null) => void;
  months: MonthOption[];
  selectedMonthKey: string | null;
  onSelectMonth: (key: string | null) => void;
}

const chipCls = (active: boolean): string =>
  `flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 ${
    active
      ? 'text-black border-transparent shadow-[0_0_14px_-2px_rgba(63,206,209,0.45)]'
      : 'bg-zinc-900 text-gray-400 border-zinc-800 hover:bg-zinc-800 hover:text-gray-200'
  }`;

/**
 * Sticky Filter-Leiste: Zeitraum-Toggle, Suche und horizontale
 * Kategorie-/Organisations-Chips (Muster: DaySelector).
 */
const EventsFilterBar = forwardRef<HTMLDivElement, EventsFilterBarProps>((props, ref) => {
  const {
    mode,
    onModeChange,
    searchTerm,
    onSearchChange,
    categories,
    selectedCategoryId,
    onSelectCategory,
    organizations,
    selectedOrg,
    onSelectOrg,
    months,
    selectedMonthKey,
    onSelectMonth,
  } = props;

  const hasChips = categories.length > 0 || organizations.length > 0;

  // Monats-Leiste: horizontales Scrollen per Pfeil-Buttons, aktiver Monat sichtbar
  const monthScrollRef = useRef<HTMLDivElement>(null);

  const scrollMonths = (dir: -1 | 1) => {
    monthScrollRef.current?.scrollBy({ left: dir * 260, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!selectedMonthKey || !monthScrollRef.current) return;
    const el = monthScrollRef.current.querySelector<HTMLElement>(
      `[data-month="${selectedMonthKey}"]`,
    );
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [selectedMonthKey]);

  return (
    <div
      ref={ref}
      className="sticky top-16 z-30 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800/80"
    >
      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-2.5 space-y-2">
        {/* Zeile 1: Zeitraum-Toggle + Suche */}
        <div className="flex items-center gap-2">
          <div className="inline-flex flex-shrink-0 bg-zinc-900 border border-zinc-800 rounded-xl p-1">
            <button
              onClick={() => onModeChange('upcoming')}
              className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                mode === 'upcoming' ? 'text-black' : 'text-gray-400 hover:text-gray-200'
              }`}
              style={{ backgroundColor: mode === 'upcoming' ? ACCENT : 'transparent' }}
              aria-pressed={mode === 'upcoming'}
              aria-label="Kommende Events"
            >
              <CalendarDays size={14} />
              <span className="hidden sm:inline">Kommende</span>
            </button>
            <button
              onClick={() => onModeChange('past')}
              className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                mode === 'past' ? 'text-black' : 'text-gray-400 hover:text-gray-200'
              }`}
              style={{ backgroundColor: mode === 'past' ? ACCENT : 'transparent' }}
              aria-pressed={mode === 'past'}
              aria-label="Vergangene Events"
            >
              <History size={14} />
              <span className="hidden sm:inline">Vergangene</span>
            </button>
          </div>

          <div className="relative flex-1 min-w-0">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Events durchsuchen…"
              aria-label="Events durchsuchen"
              className="w-full h-9 pl-9 pr-8 rounded-xl bg-zinc-900 border border-zinc-800 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#3fced1]/60 transition-colors"
            />
            {searchTerm.length > 0 && (
              <button
                onClick={() => onSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-500 hover:text-white hover:bg-zinc-800 transition-colors"
                aria-label="Suche löschen"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Zeile 2: Kategorie- und Organisations-Chips */}
        {hasChips && (
          <div
            className="flex items-center gap-1.5 overflow-x-auto py-0.5 [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: 'none' }}
          >
            <button
              onClick={() => {
                onSelectCategory(null);
                onSelectOrg(null);
              }}
              className={chipCls(selectedCategoryId === null && selectedOrg === null)}
              style={{
                backgroundColor:
                  selectedCategoryId === null && selectedOrg === null ? ACCENT : undefined,
              }}
            >
              Alle
            </button>

            {categories.map((cat) => {
              const id = cat.id || cat._id || '';
              if (!id || !cat.name) return null;
              const active = selectedCategoryId === id;
              return (
                <button
                  key={id}
                  onClick={() => onSelectCategory(active ? null : id)}
                  className={chipCls(active)}
                  style={{ backgroundColor: active ? ACCENT : undefined }}
                >
                  {cat.name}
                </button>
              );
            })}

            {categories.length > 0 && organizations.length > 0 && (
              <span className="flex-shrink-0 w-px h-5 bg-zinc-800 mx-1" aria-hidden="true" />
            )}

            {organizations.map((org) => {
              const active = selectedOrg === org;
              return (
                <button
                  key={org}
                  onClick={() => onSelectOrg(active ? null : org)}
                  className={chipCls(active)}
                  style={{ backgroundColor: active ? ACCENT : undefined }}
                >
                  {org}
                </button>
              );
            })}
          </div>
        )}

        {/* Zeile 3: Monats-Leiste (Sprung-Filter, PDC-Stil) */}
        {months.length > 0 && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => scrollMonths(-1)}
              className="hidden sm:flex flex-shrink-0 items-center justify-center w-8 h-10 rounded-xl text-gray-500 hover:text-white hover:bg-zinc-800 transition-colors"
              aria-label="Frühere Monate anzeigen"
            >
              <ChevronLeft size={18} />
            </button>

            <div
              ref={monthScrollRef}
              className="flex items-center gap-1.5 overflow-x-auto py-0.5 flex-1 min-w-0 [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: 'none' }}
            >
              {months.map((m) => {
                const active = selectedMonthKey === m.key;
                return (
                  <button
                    key={m.key}
                    data-month={m.key}
                    onClick={() => onSelectMonth(active ? null : m.key)}
                    className={`flex-shrink-0 px-4 h-10 rounded-xl text-xs font-extrabold uppercase tracking-wider border transition-all duration-200 ${
                      active
                        ? 'text-black border-transparent shadow-[0_0_14px_-2px_rgba(63,206,209,0.45)]'
                        : 'bg-zinc-900 text-gray-300 border-zinc-800 hover:bg-zinc-800 hover:text-white'
                    }`}
                    style={{ backgroundColor: active ? ACCENT : undefined }}
                    aria-pressed={active}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => scrollMonths(1)}
              className="hidden sm:flex flex-shrink-0 items-center justify-center w-8 h-10 rounded-xl text-gray-500 hover:text-white hover:bg-zinc-800 transition-colors"
              aria-label="Spätere Monate anzeigen"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

EventsFilterBar.displayName = 'EventsFilterBar';

export default EventsFilterBar;
